import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import config from '../../../constants/apiConfig';
import { useSelector } from 'react-redux';
import { selectUserGuid } from '../../../slices/commonSelectors';
import { toast } from 'react-toastify';
import { useActualIncidentId } from './commonHooks';
import { ComsBase } from '../../../types/iapBaseTypes';
import { IapScope, INCIDENT } from '../ICSConstants';

export type IncidentComs = ComsBase & {
  id: string;
  coms_id: string;
  incident_id: string;
  user_guid: string;
  version: number;
  timestamp: Date;
  name?: string;
  branch_name?: string;
  division_name?: string;
};

export const keys = {
  base: 'incident_coms',
  get all() {
    return [keys.base];
  },
  list: (incidentId: string, userId: string) => [keys.base, incidentId, userId],
} as const;

export type CreateIncidentComsReqData = ComsBase & {
  coms_id?: string;
  incident_id?: string;
  user_guid?: string;
  name?: string;
  work_group_id?: string;
  group_guid?: string;
};

export interface EditIncidentComsReqData extends ComsBase {
  id: string;
  name?: string;
}

export type UpdateIncidentComsVariables = {
  comsId: string;
  data: EditIncidentComsReqData;
};

// Hook to fetch incident communications
export const useIncidentComs = (scope: IapScope) => {
  const incidentId = useActualIncidentId();
  const user_guid = useSelector(selectUserGuid);
  return useQuery({
    queryKey: keys.list(incidentId, user_guid),
    queryFn: async () => {
      const resp = await axios.get<IncidentComs[]>(
        `${config.apiGateway.ics_incident_coms}/${incidentId}`
      );
      return resp.data;
    },
    enabled: scope === INCIDENT,
  });
};

// Hook to create incident communication
export const useCreateIncidentComs = () => {
  const incidentId = useActualIncidentId();
  const user_guid = useSelector(selectUserGuid);
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (data: CreateIncidentComsReqData) => {
      // First, create the organization-level coms object
      const orgData = {
        ...data,
        incident_id: undefined,
      };
      const orgComs = await createComs(orgData);

      const coms_id = orgComs.id;
      return await axios.post(
        `${config.apiGateway.ics_incident_coms}/${incidentId}`,
        { ...data, incident_id: incidentId, user_guid, coms_id }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: keys.list(incidentId, user_guid),
      });
      toast.success('Communication created successfully');
    },
    onError: (error: any) => {
      toast.error(error.message);
    },
  });
  return mutation;
};

// Hook to update any incident communication (for tables; pass comsId in mutation)
export const useUpdateIncidentComs = () => {
  const user_guid = useSelector(selectUserGuid);
  const incidentId = useActualIncidentId();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ comsId, data }: UpdateIncidentComsVariables) => {
      return await axios.put<UpdateIncidentComsVariables['data']>(
        `${config.apiGateway.ics_incident_coms}/${incidentId}/${comsId}`,
        { ...data, user_guid }
      );
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: keys.list(incidentId, user_guid),
      });
      if (data.data.archived) {
        toast.success('Communication deleted successfully');
      } else {
        toast.success('Communication updated successfully');
      }
    },
    onError: (error: any) => {
      toast.error(error.message);
    },
  });
};

// Function to create organization-level coms object
export async function createComs(data: any) {
  try {
    const id = uuidv4();
    const result = await axios.post(`${config.apiGateway.ics_coms}`, {
      ...data,
      id,
    });
    return result.data;
  } catch (error: any) {
    throw new Error(`Error creating coms: ${error.message}`);
  }
}

export const getComsTitle = (coms: IncidentComs) => {
  let formattedName = `${coms.type}: Branch (${coms.branch_name}) - Div (${coms.division_name})`;
  if (coms.type === 'Radio') {
    formattedName += ` | Fn: ${coms.function} | Freq: ${coms.rx_frequency}`;
  } else {
    const method = coms.cell_phone_number || coms.other || 'N/A';
    formattedName += ` | Name: ${coms.name} | Method: ${method}`;
  }
  return formattedName;
};
